import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box
} from '@mui/material';
import {
  Close as CloseIcon,
  Smartphone as SmartphoneIcon,
  Info as InfoIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon,
  ScreenRotation as ScreenRotationIcon,
  BatteryChargingFull as BatteryIcon,
  Wifi as WifiIcon,
  Language as LanguageIcon,
  Dns as DnsIcon
} from '@mui/icons-material';

interface DeviceInfoModalProps {
  open: boolean;
  onClose: () => void;
  deviceInfo: any | null;
}

const DeviceInfoModal: React.FC<DeviceInfoModalProps> = ({ open, onClose, deviceInfo }) => {
  if (!deviceInfo) {
    return null;
  }

  // Cihaz türüne göre ikonu belirle
  const getDeviceIcon = () => {
    return <SmartphoneIcon fontSize="large" color="primary" />;
  };

  // Pil seviyesi rengini belirle
  const getBatteryColor = (level: number) => {
    if (level > 50) return 'success.main';
    if (level > 20) return 'warning.main';
    return 'error.main';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        Cihaz Bilgileri
        <IconButton
          aria-label="kapat"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'primary.contrastText' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ mt: 2, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          {getDeviceIcon()}
          <Box sx={{ ml: 2 }}>
            <Typography variant="h6" component="div">
              {deviceInfo.model || deviceInfo.deviceType || 'Bilinmeyen Cihaz'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {deviceInfo.manufacturer || 'Üretici bilgisi yok'}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <List disablePadding>
          {/* İşletim Sistemi Bilgisi */}
          {deviceInfo.platform && (
            <>
              <ListItem sx={{ px: 0 }}>
                <InfoIcon color="primary" sx={{ mr: 2 }} />
                <ListItemText
                  primary="İşletim Sistemi"
                  secondary={`${deviceInfo.platform} ${deviceInfo.osVersion || ''}`}
                />
              </ListItem>
              <Divider component="li" />
            </>
          )}

          {/* Bellek Bilgisi */}
          {deviceInfo.memory && (
            <>
              <ListItem sx={{ px: 0 }}>
                <MemoryIcon color="primary" sx={{ mr: 2 }} />
                <ListItemText
                  primary="Bellek (RAM)"
                  secondary={`${Math.round(deviceInfo.memory / 1024)} MB`}
                />
              </ListItem>
              <Divider component="li" />
            </>
          )}

          {/* İşlemci Bilgisi */}
          {deviceInfo.cpuCores && (
            <>
              <ListItem sx={{ px: 0 }}>
                <SpeedIcon color="primary" sx={{ mr: 2 }} />
                <ListItemText
                  primary="İşlemci"
                  secondary={`${deviceInfo.cpuCores} Çekirdek`}
                />
              </ListItem>
              <Divider component="li" />
            </>
          )}

          {/* Depolama Bilgisi */}
          {deviceInfo.diskSpace && (
            <>
              <ListItem sx={{ px: 0 }}>
                <StorageIcon color="primary" sx={{ mr: 2 }} />
                <ListItemText
                  primary="Depolama Alanı"
                  secondary={`${Math.round(deviceInfo.diskSpace / (1024 * 1024))} GB`}
                />
              </ListItem>
              <Divider component="li" />
            </>
          )}

          {/* Ekran Bilgisi */}
          {deviceInfo.screenWidth && deviceInfo.screenHeight && (
            <>
              <ListItem sx={{ px: 0 }}>
                <ScreenRotationIcon color="primary" sx={{ mr: 2 }} />
                <ListItemText
                  primary="Ekran Çözünürlüğü"
                  secondary={`${deviceInfo.screenWidth} x ${deviceInfo.screenHeight} piksel`}
                />
              </ListItem>
              <Divider component="li" />
            </>
          )}

          {/* Pil Durumu */}
          {deviceInfo.batteryLevel !== undefined && (
            <>
              <ListItem sx={{ px: 0 }}>
                <BatteryIcon sx={{ mr: 2, color: getBatteryColor(deviceInfo.batteryLevel) }} />
                <ListItemText
                  primary="Pil Durumu"
                  secondary={`%${deviceInfo.batteryLevel}`}
                />
              </ListItem>
              <Divider component="li" />
            </>
          )}

          {/* Ağ Bilgisi */}
          {deviceInfo.networkType && (
            <>
              <ListItem sx={{ px: 0 }}>
                <WifiIcon color="primary" sx={{ mr: 2 }} />
                <ListItemText
                  primary="Ağ Bağlantısı"
                  secondary={deviceInfo.networkType}
                />
              </ListItem>
              <Divider component="li" />
            </>
          )}

          {/* Dil ve Bölge */}
          {deviceInfo.language && (
            <>
              <ListItem sx={{ px: 0 }}>
                <LanguageIcon color="primary" sx={{ mr: 2 }} />
                <ListItemText
                  primary="Dil ve Bölge"
                  secondary={`${deviceInfo.language} / ${deviceInfo.region || 'Bilinmiyor'}`}
                />
              </ListItem>
              <Divider component="li" />
            </>
          )}

          {/* Uygulama Versiyonu */}
          {deviceInfo.appVersion && (
            <>
              <ListItem sx={{ px: 0 }}>
                <InfoIcon color="primary" sx={{ mr: 2 }} />
                <ListItemText
                  primary="Uygulama Versiyonu"
                  secondary={deviceInfo.appVersion}
                />
              </ListItem>
              <Divider component="li" />
            </>
          )}

          {/* Cihaz ID */}
          {deviceInfo.deviceId && (
            <>
              <ListItem sx={{ px: 0 }}>
                <DnsIcon color="primary" sx={{ mr: 2 }} />
                <ListItemText
                  primary="Cihaz ID"
                  secondary={deviceInfo.deviceId}
                />
              </ListItem>
            </>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default DeviceInfoModal; 